import React from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { Auth0Provider } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { AppUpdater } from '~/components/layout/app-updater';
import { Toast } from '~/components/ui/toast';
import { TooltipProvider } from '~/components/ui/tooltip';
import { pageView } from '~/libs/analytics';
import '~/libs/i18n';
import '~/libs/instrument';
import { createRouter, queryClient } from '~/router';

import '~/styles.css';

const router = createRouter();

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById('root')!;

if (!rootElement.innerHTML) {
  const root = createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <Auth0Provider
        domain={import.meta.env.REACT_APP_AUTH0_DOMAIN as string}
        clientId={import.meta.env.REACT_APP_AUTH0_CLIENT_ID as string}
        authorizationParams={{
          audience: `${import.meta.env.REACT_APP_AUTH0_AUDIENCE as string}`,
          redirect_uri: `${window.location.origin}/auth-redirect`,
          scope: 'openid profile email offline_access',
        }}
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <RouterProvider router={router} />
            <Toast />
            <AppUpdater />
          </TooltipProvider>
        </QueryClientProvider>
      </Auth0Provider>
    </StrictMode>,
  );
}

router.history.subscribe((url) => {
  const pathname = url.location.pathname;

  if (pathname === '/aitools') {
    pageView('AiTools');
    return;
  }

  if (pathname === '/calendar') {
    pageView('Calendar');
    return;
  }

  if (pathname === '/discount') {
    pageView('Discount');
    return;
  }

  if (pathname === '/marketing') {
    pageView('Marketing');
    return;
  }

  if (pathname.startsWith('/marque/')) {
    pageView('Brand');
    return;
  }

  if (pathname.startsWith('/setup/')) {
    pageView('Setup');
    return;
  }

  if (pathname.startsWith('/vente/')) {
    pageView('Sale');
    return;
  }

  if (pathname === '/ventes') {
    pageView('Sales');
    return;
  }

  if (pathname === '/unauthorized') {
    pageView('Unauthorized');
    return;
  }

  pageView(pathname);
});
